import {
  ArrowLeftIcon,
  EyeNoneIcon,
  EyeOpenIcon,
  LockClosedIcon,
  MobileIcon,
} from '@radix-ui/react-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
  forgotPassword,
  login,
  resendOtp,
  resetPassword,
  verifyOtp as verifyOtpRequest,
} from '@features/auth/api/auth';
import { useAuthStore } from '@features/auth/store/authStore';
import type { CurrentUser } from '@features/auth/store/authStore';
import { AuthFrame, AuthHeading, AuthResult } from '../components/AuthExperience';
import { playAuthKeySound } from '../components/authKeySound';
import { useAuthVisual } from '../components/authVisual';
import { NormalizedApiError } from '@shared/api/client';
import { OtpCodeInput } from '@features/auth/components/OtpCodeInput';

const loginSchema = z.object({
  username: z.string().trim().min(1, { error: 'auth.enterCredentials' }),
  password: z.string().min(8, { error: 'validation.password' }),
});
type LoginValues = z.infer<typeof loginSchema>;
const otpSchema = z.object({ code: z.string().regex(/^\d{6}$/, { error: 'validation.otp' }) });
type OtpValues = z.infer<typeof otpSchema>;
const forgotPasswordSchema = z.object({
  username: z.string().trim().min(1, { error: 'validation.username' }),
});
type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;
const resetPasswordSchema = z.object({
  code: z.string().regex(/^\d{6}$/, { error: 'validation.otp' }),
  newPassword: z.string().min(8, { error: 'validation.password' }),
});
type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

export function AuthLayout() {
  return (
    <AuthFrame>
      <Outlet />
    </AuthFrame>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const beginLogin = useAuthStore((state) => state.beginLogin);
  const updateVisual = useAuthVisual();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });
  const email = useWatch({ control, name: 'username' });
  const passwordLength = useWatch({ control, name: 'password' }).length;
  useEffect(() => {
    updateVisual({ email, passwordMask: '•'.repeat(passwordLength) });
  }, [email, passwordLength, updateVisual]);
  const expired = (location.state as { otpExpired?: boolean } | null)?.otpExpired === true;
  const submit = handleSubmit(async (values) => {
    setLoginError(null);
    try {
      const response = await login(values);
      if (!response.isSuccess || response.data === null) {
        setLoginError(response.message || t('auth.invalidCredentials'));
        return;
      }
      beginLogin(
        response.data.challengeId,
        response.data.maskedDestination,
        response.data.expiresAtUtc,
      );
      void navigate('/verify-otp');
    } catch {
      setLoginError(t('auth.loginUnavailable'));
    }
  });
  const message =
    loginError ??
    (errors.username
      ? t('auth.enterCredentials')
      : errors.password
        ? t(errors.password.message ?? 'validation.password')
        : expired
          ? t('auth.otpExpired')
          : null);
  return (
    <form
      className="auth-form"
      onSubmit={(event) => void submit(event)}
      noValidate
      onKeyDownCapture={(event) => {
        if (
          event.target instanceof HTMLInputElement &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.altKey &&
          (event.key.length === 1 || event.key === 'Backspace')
        )
          playAuthKeySound();
      }}
    >
      <AuthHeading title={t('auth.welcome')} description={t('auth.subtitle')} />
      <div className="auth-fields">
        <label className="auth-field">
          <span className="auth-field-label">{t('auth.email')}</span>
          <span className="auth-control">
            <Controller
              name="username"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="email"
                  autoComplete="username"
                  placeholder="you@company.com"
                  aria-invalid={!!errors.username}
                  autoCapitalize="none"
                  spellCheck={false}
                />
              )}
            />
          </span>
        </label>
        <label className="auth-field">
          <span className="auth-field-label">{t('auth.password')}</span>
          <span className="auth-control">
            <LockClosedIcon width={15} height={15} aria-hidden="true" />
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  aria-invalid={!!errors.password}
                />
              )}
            />
            <button
              className="auth-eye"
              type="button"
              aria-label={t(showPassword ? 'auth.hidePassword' : 'auth.showPassword')}
              aria-pressed={showPassword}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeNoneIcon width={15} height={15} />
              ) : (
                <EyeOpenIcon width={15} height={15} />
              )}
            </button>
          </span>
        </label>
        {message && (
          <p className="auth-error" role="alert">
            {message}
          </p>
        )}
        <div className="auth-links auth-forgot">
          <RouterLink className="auth-link" to="/forgot-password">
            {t('auth.forgot')}
          </RouterLink>
        </div>
        <button
          className="auth-primary auth-submit"
          type="submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting && <span className="auth-spinner" aria-hidden="true" />}
          {t('auth.signIn')}
        </button>
      </div>
      <p className="auth-note">{t('auth.passwordNote')}</p>
    </form>
  );
}

export function VerifyOtpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const completeLogin = useAuthStore((state) => state.completeLogin);
  const beginLogin = useAuthStore((state) => state.beginLogin);
  const clearOtpChallenge = useAuthStore((state) => state.clearOtpChallenge);
  const returnTo = useAuthStore((state) => state.returnTo);
  const challengeId = useAuthStore((state) => state.otpChallengeId);
  const destination = useAuthStore((state) => state.otpDestination);
  const expiresAtUtc = useAuthStore((state) => state.otpExpiresAtUtc);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [verifiedUser, setVerifiedUser] = useState<CurrentUser | null>(null);
  const [resendAvailableAt, setResendAvailableAt] = useState(() => Date.now() + 42_000);
  const [now, setNow] = useState(() => Date.now());
  const resendSeconds = Math.max(0, Math.ceil((resendAvailableAt - now) / 1000));
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OtpValues>({ resolver: zodResolver(otpSchema), defaultValues: { code: '' } });

  useEffect(() => {
    if (verifiedUser) return;
    if (challengeId === null || isPast(expiresAtUtc)) {
      clearOtpChallenge();
      void navigate('/login', { replace: true, state: { otpExpired: true } });
      return;
    }
    const timer = setTimeout(
      () => {
        clearOtpChallenge();
        void navigate('/login', { replace: true, state: { otpExpired: true } });
      },
      Math.max(0, Date.parse(expiresAtUtc!) - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [challengeId, clearOtpChallenge, expiresAtUtc, navigate, verifiedUser]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!verifiedUser) return;
    const timer = setTimeout(() => {
      completeLogin(verifiedUser);
      void navigate(returnTo, { replace: true });
    }, 700);
    return () => clearTimeout(timer);
  }, [completeLogin, navigate, returnTo, verifiedUser]);

  const expireOtp = (message = t('auth.otpExpired')) => {
    clearOtpChallenge();
    setOtpError(message);
    void navigate('/login', { replace: true, state: { otpExpired: true } });
  };

  const submit = handleSubmit(async ({ code }) => {
    if (challengeId === null) {
      expireOtp();
      return;
    }

    setOtpError(null);
    try {
      const response = await verifyOtpRequest(challengeId, code);
      if (!response.isSuccess || response.data === null) {
        if (response.status === 410 || response.errorCode === 'OTP_EXPIRED') {
          expireOtp(response.message);
          return;
        }
        setOtpError(response.message);
        return;
      }

      const user = response.data.user;
      setVerifiedUser({
        id: user.id,
        name: user.displayName,
        email: user.email ?? '',
        role: user.role,
        language: user.language,
        departmentId: user.departmentId,
        departmentName: user.departmentName,
        permissions: user.permissions,
      });
    } catch (error) {
      if (isOtpExpiredError(error)) {
        expireOtp(error.message);
        return;
      }
      setOtpError(error instanceof Error ? error.message : t('auth.otpInvalid'));
    }
  });

  const resend = async () => {
    if (resending || resendSeconds > 0 || verifiedUser) return;
    if (challengeId === null) {
      expireOtp();
      return;
    }
    setResending(true);
    setOtpError(null);
    try {
      const response = await resendOtp(challengeId);
      if (!response.isSuccess || response.data === null) {
        if (response.status === 410 || response.errorCode === 'OTP_EXPIRED') {
          expireOtp(response.message);
          return;
        }
        setOtpError(response.message);
        return;
      }
      beginLogin(
        response.data.challengeId,
        response.data.maskedDestination,
        response.data.expiresAtUtc,
      );
      setResendAvailableAt(Date.now() + 42_000);
      setNow(Date.now());
    } catch (error) {
      if (isOtpExpiredError(error)) {
        expireOtp(error.message);
        return;
      }
      setOtpError(error instanceof Error ? error.message : t('auth.loginUnavailable'));
    } finally {
      setResending(false);
    }
  };
  if (verifiedUser)
    return <AuthResult success title={t('auth.successTitle')} message={t('auth.successBody')} />;
  const ending = destination?.replace(/\D/g, '').slice(-2);
  const otpMessage = otpError ?? (errors.code ? t('auth.enterOtp') : null);
  return (
    <form className="auth-form" onSubmit={(event) => void submit(event)} noValidate>
      <AuthHeading
        title={t('auth.otpTitle')}
        icon={<MobileIcon width={22} height={22} />}
        description={
          ending ? t('auth.otpDestination', { destination: '•• ' + ending }) : t('auth.otpSubtitle')
        }
      />
      <Controller
        name="code"
        control={control}
        render={({ field }) => (
          <OtpCodeInput
            value={field.value}
            label={t('auth.otp')}
            error={!!errors.code || !!otpError}
            onBlur={field.onBlur}
            onChange={(value) => {
              field.onChange(value);
              setOtpError(null);
            }}
            autoFocus
          />
        )}
      />
      {otpMessage && (
        <p className="auth-error auth-form-error" role="alert">
          {otpMessage}
        </p>
      )}
      <button
        className="auth-primary"
        type="submit"
        disabled={isSubmitting || resending}
        aria-busy={isSubmitting}
      >
        {isSubmitting && <span className="auth-spinner" aria-hidden="true" />}
        {t('auth.verify')}
      </button>
      <div className="auth-resend">
        {resendSeconds > 0 ? (
          t('auth.resendIn', { time: '0:' + String(resendSeconds).padStart(2, '0') })
        ) : (
          <button
            className="auth-link"
            type="button"
            disabled={resending || isSubmitting}
            onClick={() => void resend()}
          >
            {t('auth.resend')}
          </button>
        )}
      </div>
      <div className="auth-links auth-back">
        <button
          className="auth-link"
          type="button"
          disabled={isSubmitting || resending}
          onClick={() => {
            clearOtpChallenge();
            void navigate('/login', { replace: true });
          }}
        >
          <ArrowLeftIcon aria-hidden="true" />
          {t('auth.differentAccount')}
        </button>
      </div>
    </form>
  );
}

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const beginPasswordReset = useAuthStore((state) => state.beginPasswordReset);
  const [resetError, setResetError] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { username: '' },
  });
  const submit = handleSubmit(async ({ username }) => {
    setResetError(null);
    try {
      const response = await forgotPassword(username);
      if (!response.isSuccess || response.data === null) {
        setResetError(response.message);
        return;
      }

      beginPasswordReset(
        response.data.challengeId,
        response.data.maskedDestination,
        response.data.expiresAtUtc,
      );
      void navigate('/reset-password');
    } catch (error) {
      setResetError(error instanceof Error ? error.message : t('auth.loginUnavailable'));
    }
  });
  return (
    <form className="auth-form" onSubmit={(event) => void submit(event)} noValidate>
      <AuthHeading title={t('auth.resetTitle')} description={t('auth.resetSubtitle')} />
      <div className="auth-fields">
        <Controller
          name="username"
          control={control}
          render={({ field }) => (
            <label className="auth-field">
              <span className="auth-field-label">{t('auth.username')}</span>
              <span className="auth-control">
                <input {...field} autoComplete="username" aria-invalid={!!errors.username} />
              </span>
            </label>
          )}
        />
        {(resetError || errors.username) && (
          <p className="auth-error" role="alert">
            {resetError ?? t(errors.username?.message ?? 'validation.username')}
          </p>
        )}
        <button
          className="auth-primary"
          type="submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {t('auth.sendReset')}
        </button>
        <RouterLink className="auth-link" to="/login">
          {t('auth.backSignIn')}
        </RouterLink>
      </div>
    </form>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const completeLogin = useAuthStore((state) => state.completeLogin);
  const clearPasswordResetChallenge = useAuthStore((state) => state.clearPasswordResetChallenge);
  const challengeId = useAuthStore((state) => state.passwordResetChallengeId);
  const destination = useAuthStore((state) => state.passwordResetDestination);
  const expiresAtUtc = useAuthStore((state) => state.passwordResetExpiresAtUtc);
  const [resetError, setResetError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { code: '', newPassword: '' },
  });

  useEffect(() => {
    if (challengeId === null || isPast(expiresAtUtc)) {
      clearPasswordResetChallenge();
      void navigate('/forgot-password', { replace: true, state: { otpExpired: true } });
    }
  }, [challengeId, clearPasswordResetChallenge, expiresAtUtc, navigate]);

  const expireResetOtp = (message = t('auth.otpExpired')) => {
    clearPasswordResetChallenge();
    setResetError(message);
    void navigate('/forgot-password', { replace: true, state: { otpExpired: true } });
  };

  const submit = handleSubmit(async ({ code, newPassword }) => {
    if (challengeId === null) {
      expireResetOtp();
      return;
    }

    setResetError(null);
    try {
      const response = await resetPassword(challengeId, code, newPassword);
      if (!response.isSuccess || response.data === null) {
        if (response.status === 410 || response.errorCode === 'OTP_EXPIRED') {
          expireResetOtp(response.message);
          return;
        }
        setResetError(response.message);
        return;
      }

      const user = response.data.user;
      completeLogin({
        id: user.id,
        name: user.displayName,
        email: user.email ?? '',
        role: user.role,
        language: user.language,
        departmentId: user.departmentId,
        departmentName: user.departmentName,
        permissions: user.permissions,
      });
      void navigate('/dashboard', { replace: true, state: { passwordSaved: true } });
    } catch (error) {
      if (isOtpExpiredError(error)) {
        expireResetOtp(error.message);
        return;
      }
      setResetError(error instanceof Error ? error.message : t('auth.otpInvalid'));
    }
  });

  return (
    <form className="auth-form" onSubmit={(event) => void submit(event)} noValidate>
      <AuthHeading
        title={t('auth.newPasswordTitle')}
        icon={<MobileIcon width={22} height={22} />}
        description={
          destination
            ? t('auth.otpDestination', {
                destination: '•• ' + destination.replace(/\D/g, '').slice(-2),
              })
            : t('auth.otpSubtitle')
        }
      />
      <Controller
        name="code"
        control={control}
        render={({ field }) => (
          <OtpCodeInput
            value={field.value}
            label={t('auth.otp')}
            onBlur={field.onBlur}
            onChange={field.onChange}
            error={!!errors.code}
            helperText={errors.code?.message ? t(errors.code.message) : undefined}
            autoFocus
          />
        )}
      />
      <div className="auth-fields">
        <Controller
          name="newPassword"
          control={control}
          render={({ field }) => (
            <label className="auth-field">
              <span className="auth-field-label">{t('auth.newPassword')}</span>
              <span className="auth-control">
                <LockClosedIcon width={15} height={15} aria-hidden="true" />
                <input
                  {...field}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  aria-invalid={!!errors.newPassword}
                />
                <button
                  className="auth-eye"
                  type="button"
                  aria-label={t(showPassword ? 'auth.hidePassword' : 'auth.showPassword')}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeNoneIcon /> : <EyeOpenIcon />}
                </button>
              </span>
            </label>
          )}
        />
        {(resetError || errors.newPassword) && (
          <p className="auth-error" role="alert">
            {resetError ?? t(errors.newPassword?.message ?? 'validation.password')}
          </p>
        )}
        <button
          className="auth-primary"
          type="submit"
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {t('auth.savePassword')}
        </button>
        <RouterLink className="auth-link" to="/login">
          {t('auth.backSignIn')}
        </RouterLink>
      </div>
    </form>
  );
}

function isPast(value: string | null): boolean {
  return value === null || Number.isNaN(Date.parse(value)) || Date.parse(value) <= Date.now();
}

function isOtpExpiredError(error: unknown): error is NormalizedApiError {
  return (
    error instanceof NormalizedApiError && (error.status === 410 || error.code === 'OTP_EXPIRED')
  );
}
